import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Briefcase, GraduationCap, HeartHandshake, Zap, Target } from "lucide-react";

export default function LavoraConNoi() {
  const [formState, setFormState] = useState({ 
    nome: "", 
    cognome: "", 
    azienda: "", 
    email: "", 
    telefono: "", 
    citta: "", 
    presentazione: "" 
  });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    if (!file) {
      setErrorMessage("Per favore, allega il tuo CV in formato PDF.");
      setStatus("error");
      return;
    }

    const formData = new FormData();
    Object.keys(formState).forEach(key => {
      formData.append(key, formState[key]);
    });
    formData.append("file", file);

    try {
      const res = await fetch(`/api/candidature`, {
        method: "POST",
        body: formData
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Errore durante l'invio");
      }
      
      setStatus("success");
      setFormState({ nome: "", cognome: "", azienda: "", email: "", telefono: "", citta: "", presentazione: "" });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setStatus("idle"), 3000);
    } catch (error) {
      console.error(error);
      setStatus("error");
      setErrorMessage(error.message || "Impossibile inviare la candidatura. Riprova più tardi.");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const advantages = [
    { icon: <Briefcase size={32} className="text-[#7dba00]" />, title: "Condizioni Esclusive", desc: "Accesso a prodotti premium e condizioni commerciali riservate ai nostri partner." },
    { icon: <Target size={32} className="text-[#7dba00]" />, title: "Supporto Marketing", desc: "Materiali promozionali, campagne congiunte e supporto per l'allestimento dello showroom." },
    { icon: <GraduationCap size={32} className="text-[#7dba00]" />, title: "Formazione Tecnica", desc: "Corsi di aggiornamento sui nuovi sistemi Schüco e supporto costante in fase di progettazione e posa." },
    { icon: <Zap size={32} className="text-[#7dba00]" />, title: "Innovazione Continua", desc: "Accesso in anteprima a tecnologie all'avanguardia per l'efficienza energetica e la domotica." },
    { icon: <Users size={32} className="text-[#7dba00]" />, title: "Rete di Contatti", desc: "Inserimento in un network di professionisti, architetti e progettisti di alto livello." },
    { icon: <HeartHandshake size={32} className="text-[#7dba00]" />, title: "Assistenza Dedicata", desc: "Un canale diretto di comunicazione per risolvere rapidamente ogni esigenza di cantiere." }
  ];

  return (
    <div className="w-full bg-white text-black min-h-screen">
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-24 px-6 lg:px-12 bg-white text-black">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 items-center">
            <div className="w-full md:w-1/2 pr-0 md:pr-8 z-10">
              <motion.h1 
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                className="text-5xl md:text-6xl lg:text-[6rem] font-bold tracking-tighter mb-8 uppercase leading-[1]"
              >
                Partnership<br/><span className="text-[#7dba00]">Izosmart</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}
                className="text-xl md:text-2xl font-light text-gray-600 max-w-xl leading-relaxed"
              >
                Hai esperienza nel campo della casa e lavori con serramenti, zanzariere, porte, ecc.? 
                Unisciti alla nostra squadra: ti offriremo i servizi e i pacchetti migliori!
              </motion.p>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}
              className="w-full md:w-1/2 max-w-md mx-auto aspect-[4/3] bg-gray-100 overflow-hidden relative shadow-lg z-0"
            >
              <img 
                src="/media/partner-office.jpg" 
                alt="Partnership Team" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000 ease-out" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="py-24 md:py-32 px-6 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-10 leading-tight">
              Cresci insieme a Izosmart
            </h2>
            <div className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed space-y-6">
              <p>
                Il progetto di partnership di Izosmart si propone di instaurare <strong>una collaborazione strategica nel settore dei serramenti di alta gamma</strong>. L'obiettivo è creare una sinergia che favorisca la crescita e l'innovazione, garantendo la massima qualità dei prodotti Schüco e la totale soddisfazione del cliente.
              </p>
              <p>
                La collaborazione non si limita alla semplice fornitura, ma include un forte supporto in termini di formazione, marketing e assistenza tecnica. Avrai al tuo fianco un partner solido che ti aiuterà a raggiungere i tuoi obiettivi di business.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ADVANTAGES GRID */}
      <section className="py-24 md:py-32 bg-gray-50 border-y border-gray-100 px-6 lg:px-12">
        <div className="container mx-auto max-w-6xl">
          <motion.h2 
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="text-3xl md:text-5xl font-bold tracking-tighter mb-20 text-center uppercase"
          >
            I Vantaggi della Partnership
          </motion.h2>
          
          <motion.div 
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16"
          >
            {advantages.map((adv) => (
              <motion.div key={adv.title} variants={fadeInUp} className="bg-white p-10 shadow-sm hover:shadow-xl transition-shadow duration-300 border-t-4 border-transparent hover:border-[#7dba00]">
                <div className="mb-6 bg-gray-50 w-16 h-16 flex items-center justify-center rounded-full">
                  {adv.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{adv.title}</h3>
                <p className="text-gray-600 font-light leading-relaxed">{adv.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* REQUIREMENTS & FORM SECTION */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-black text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-20 lg:gap-32 items-start">
            
            {/* Left side: Requirements */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-12">Cosa cerchiamo?</h2>
              <p className="text-xl text-gray-400 font-light mb-10">
                Selezioniamo professionisti ambiziosi che condividano i nostri valori di eccellenza e qualità.
              </p>
              <ul className="space-y-8 text-gray-300 font-light text-xl leading-relaxed">
                <li className="flex items-start gap-6 group">
                  <span className="font-bold text-[#7dba00] group-hover:translate-x-2 transition-transform duration-300">—</span>
                  <p>Preferibilmente nel nord d'Italia.</p>
                </li>
                <li className="flex items-start gap-6 group">
                  <span className="font-bold text-[#7dba00] group-hover:translate-x-2 transition-transform duration-300">—</span>
                  <p>Esperienza consolidata nel campo dei serramenti e della casa.</p>
                </li>
                <li className="flex items-start gap-6 group">
                  <span className="font-bold text-[#7dba00] group-hover:translate-x-2 transition-transform duration-300">—</span>
                  <p>Buone conoscenze e logistica nella vostra regione.</p>
                </li>
              </ul>
              
              <div className="mt-16 pt-16 border-t border-white/10">
                <h3 className="text-2xl font-bold tracking-tighter mb-6">Pronto per iniziare?</h3>
                <p className="text-gray-400 font-light text-lg">
                  Compila il modulo per inviare la tua candidatura come partner commerciale o installatore. Il nostro team valuterà il tuo profilo e ti contatterà nel minor tempo possibile.
                </p>
              </div>
            </motion.div>

            {/* Right side: Form */}
            <motion.div 
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}
              className="bg-white p-10 md:p-14 text-black shadow-2xl relative"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-[#7dba00]"></div>
              <h3 className="text-3xl font-bold tracking-tighter mb-10">Invia la tua candidatura</h3>
              <form className="space-y-8" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-6">
                  <input 
                    type="text" placeholder="Nome*" required 
                    value={formState.nome} onChange={e => setFormState({...formState, nome: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                  <input 
                    type="text" placeholder="Cognome*" required 
                    value={formState.cognome} onChange={e => setFormState({...formState, cognome: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                </div>
                
                <div>
                  <input 
                    type="text" placeholder="Ragione Sociale o Azienda" 
                    value={formState.azienda} onChange={e => setFormState({...formState, azienda: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <input 
                    type="email" placeholder="Email*" required 
                    value={formState.email} onChange={e => setFormState({...formState, email: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                  <input 
                    type="tel" placeholder="Telefono*" required 
                    value={formState.telefono} onChange={e => setFormState({...formState, telefono: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                </div>

                <div>
                  <input 
                    type="text" placeholder="Provincia / Città*" required 
                    value={formState.citta} onChange={e => setFormState({...formState, citta: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light" 
                  />
                </div>

                <div>
                  <textarea 
                    placeholder="Parlaci un po' di te e della tua esperienza*" required rows="3"
                    value={formState.presentazione} onChange={e => setFormState({...formState, presentazione: e.target.value})}
                    className="w-full bg-transparent border-b-2 border-gray-200 py-3 outline-none focus:border-[#7dba00] transition-colors text-black font-light resize-none" 
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-500 mb-3 font-bold uppercase tracking-widest">Allega Presentazione/CV (PDF)</label>
                  <input 
                    type="file" accept=".pdf" ref={fileInputRef}
                    onChange={e => setFile(e.target.files[0])}
                    className="w-full text-sm font-light file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-widest file:bg-gray-100 file:text-black hover:file:bg-[#7dba00] hover:file:text-white transition-all cursor-pointer" 
                  />
                </div>

                <div className="pt-4">
                  <button 
                    type="submit" disabled={status === 'submitting'}
                    className="bg-[#7dba00] text-white px-10 py-5 uppercase text-sm font-bold tracking-widest hover:bg-black transition-colors w-full disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? 'Invio in corso...' : status === 'success' ? 'Candidatura Inviata!' : 'Invia Candidatura'}
                  </button>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 font-bold mt-4 text-center">{errorMessage}</p>
                )}

                <p className="text-xs text-gray-400 mt-4 text-center">
                  * Campi obbligatori. Prima di inviare, leggi l'<Link to="/privacy" data-testid="lavora-privacy-link" className="underline hover:text-[#7dba00]">informativa privacy</Link>.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}
